import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useFetchShipByMmsi,
  useGetShipByImoFunction,
  useServiceAvailability,
} from '@ais-ui/api';
import { isValidIMONumber, isValidMMSINumber } from '@ais-ui/utils';
import { VesselResult } from '@ais-ui/api-spec/shipdb';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { ServiceUnavailableError } from '@ais-ui/api';

interface UseExtendedVesselInfoOptions {
  searchString: string;
  enabled?: boolean; // Optional flag to disable the hook
}

interface UseExtendedVesselInfoReturn {
  extendedVesselInfo: VesselResult | null;
  isLoading: boolean;
  error: string | null;
  hasPermission: boolean;
  refetch: () => void;
}

export const useExtendedVesselInfo = ({
  searchString,
  enabled = true,
}: UseExtendedVesselInfoOptions): UseExtendedVesselInfoReturn => {
  const { getShipByImo } = useGetShipByImoFunction();
  const fetchShipByMmsi = useFetchShipByMmsi();
  const permissions = usePermissions();
  const { isAvailable } = useServiceAvailability();

  const [isLoading, setIsLoading] = useState(false);
  const [extendedVesselInfo, setExtendedVesselInfo] =
    useState<VesselResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use refs to track the last search to prevent duplicate requests
  const lastSearchRef = useRef<string>('');
  const lastEnabledRef = useRef<boolean>(enabled);
  const lastPermissionRef = useRef<boolean>(false);

  const hasPermission = permissions.mdmAdvancedVesselInfo;
  const shipDbAvailable = isAvailable('shipdb');

  // Memoize the search logic separately from the fetch functions
  const shouldPerformSearch = useCallback(
    (
      currentSearchString: string,
      currentEnabled: boolean,
      currentHasPermission: boolean,
    ) => {
      // Check if we should skip the search
      if (!currentHasPermission || !currentEnabled || !shipDbAvailable) {
        return { shouldSearch: false, searchType: null };
      }

      // Check if this is the same search as last time
      if (
        lastSearchRef.current === currentSearchString &&
        lastEnabledRef.current === currentEnabled &&
        lastPermissionRef.current === currentHasPermission
      ) {
        return { shouldSearch: false, searchType: null };
      }

      // Determine search type
      if (isValidIMONumber(currentSearchString)) {
        return { shouldSearch: true, searchType: 'imo' };
      } else if (isValidMMSINumber(currentSearchString)) {
        return { shouldSearch: true, searchType: 'mmsi' };
      }

      return { shouldSearch: false, searchType: null };
    },
    [shipDbAvailable],
  );

  // Main search effect - only depends on the actual values, not the callbacks
  useEffect(() => {
    const { shouldSearch, searchType } = shouldPerformSearch(
      searchString,
      enabled,
      hasPermission,
    );

    // Update refs to track current state
    lastSearchRef.current = searchString;
    lastEnabledRef.current = enabled;
    lastPermissionRef.current = hasPermission;

    // If we shouldn't search, reset state and return
    if (!shouldSearch) {
      if (!hasPermission || !enabled) {
        setExtendedVesselInfo(null);
        setError(null);
      }
      setIsLoading(false);
      return;
    }

    // Perform the actual search
    const performActualSearch = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let result: VesselResult | null;

        if (searchType === 'imo') {
          const parsedImo = parseInt(searchString);
          result = await getShipByImo(parsedImo);
        } else if (searchType === 'mmsi') {
          const parsedMmsi = parseInt(searchString);
          result = await fetchShipByMmsi(parsedMmsi);
        } else {
          throw new Error('Invalid search type');
        }

        setExtendedVesselInfo(result);
      } catch (error) {
        if (!(error instanceof ServiceUnavailableError)) {
          console.error(
            `Error fetching ship by ${searchType?.toUpperCase()}:`,
            error,
          );
          setError(`Failed to fetch ship by ${searchType?.toUpperCase()}`);
        }
        setExtendedVesselInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    performActualSearch();
  }, [
    searchString,
    enabled,
    hasPermission,
    getShipByImo,
    fetchShipByMmsi,
    shouldPerformSearch,
  ]);

  // Refetch function that bypasses the duplicate check
  const refetch = useCallback(() => {
    // Force a refetch by clearing the ref
    lastSearchRef.current = '';

    const { shouldSearch, searchType } = shouldPerformSearch(
      searchString,
      enabled,
      hasPermission,
    );

    if (!shouldSearch) {
      setExtendedVesselInfo(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const performRefetch = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let result: VesselResult | null;

        if (searchType === 'imo') {
          const parsedImo = parseInt(searchString);
          result = await getShipByImo(parsedImo);
        } else if (searchType === 'mmsi') {
          const parsedMmsi = parseInt(searchString);
          result = await fetchShipByMmsi(parsedMmsi);
        } else {
          throw new Error('Invalid search type');
        }

        setExtendedVesselInfo(result);
      } catch (error) {
        if (!(error instanceof ServiceUnavailableError)) {
          console.error(
            `Error fetching ship by ${searchType?.toUpperCase()}:`,
            error,
          );
          setError(`Failed to fetch ship by ${searchType?.toUpperCase()}`);
        }
        setExtendedVesselInfo(null);
      } finally {
        setIsLoading(false);
      }
    };

    performRefetch();

    // Update refs after refetch
    lastSearchRef.current = searchString;
    lastEnabledRef.current = enabled;
    lastPermissionRef.current = hasPermission;
  }, [
    searchString,
    enabled,
    hasPermission,
    getShipByImo,
    fetchShipByMmsi,
    shouldPerformSearch,
  ]);

  return {
    extendedVesselInfo,
    isLoading,
    error,
    hasPermission,
    refetch,
  };
};
